// The `/transcript-chips/*` wire contracts — the in-session Evidence
// suggestion surface and the auto-evidence configuration it is driven by.
//
// Moved verbatim out of `src/lib/api/transcript-chips.ts`, which held its types
// and its transport in one module. The transport carved to
// `../data/api/transcript-chips.ts`; the shapes land here, in the inert zone,
// because two of them (`AutoEvidenceConfig` and `SuggestedChipCategory`) are
// read by app surfaces that are NOT moving in this slice — the chips bar, the
// source drawer, `useEvidenceChipsConfig` — and a contract those files reach
// through `/contracts` is one they can keep reaching after the residue is
// re-homed.
//
// NAME COLLISION, RECORDED RATHER THAN RESOLVED. `SuggestedChip` and
// `SuggestedChipCategory` are ALSO declared in
// `src/components/ui/transcript-chips/types.ts`, which the census pins as a
// staying surface: that module holds the same wire shapes plus a category table
// carrying `lucide-react` icon objects and `primitiveColors`, so it cannot enter
// an inert zone without a split. The two declarations are not merged here, and
// merging them is not this slice's to do — it is a C-series prerequisite the
// census already names. What this file changes is only WHERE the api module's
// copy lives.
//
// `pinKind` comes with them: a pure predicate over `EffectivePinnedQuery` with
// no transport, which is exactly what `/contracts` is for (the
// `canEditSourceList` precedent).

export type SuggestedChipCategory =
  | 'suggested_question'
  | 'examination'
  | 'investigation'
  | 'condition'
  | 'symptom_exploration'
  | 'plan'
  | 'red_flag'
  | 'clinical_review'
  /** Client-only: clinical-task document drafts surfaced outside the transcript API */
  | 'document_draft';

export interface SuggestedChip {
  id: string;
  query: string;
  label: string;
  /** Optional helper line for saved shortcuts (e.g. marketplace subtitle) */
  description?: string;
  category: SuggestedChipCategory;
  relevance?: number; // 0-1 relevance score for sizing/ordering (optional, backend may provide)
  isPermanent?: boolean; // User-pinned custom chip
  isDismissed?: boolean; // User dismissed this chip
  isUsed?: boolean; // Chip was clicked/used
  /** Clinical task id when `category` is `document_draft` (dismissal + analytics). */
  sourceTaskId?: string;
  // Slightly longer title to display on tooltip and when only one chip is visible
  title?: string;
  /**
   * FDE-8: when library-grounded, an alternative patient-directed phrasing
   * (e.g. "Ask about …", "Check for …") returned by the backend. `null` /
   * `undefined` on the ungrounded path.
   */
  patient_question?: string | null;
  /**
   * FDE-8: ids of entries in the response's `source_refs` list that grounded
   * this chip. Empty array on the ungrounded path.
   */
  source_ref_ids?: string[];
}

/**
 * FDE-8: a library-grounding source reference attached to a transcript chip.
 *
 * `page_number` is typically `null` today because the backend PDF extractor
 * still flattens documents into a single blob (page-aware chunking is a
 * deferred Phase 2). The drawer treats `null` as "open at page 1" and the
 * clinician Ctrl-F's the excerpt.
 */
export interface SourceRef {
  id: string;
  document_id: string;
  filename: string;
  excerpt: string;
  chunk_index: number;
  score?: number | null;
  page_number?: number | null;
}

export interface TranscriptChipsRequest {
  session_id: string;
  transcript_chunk: string;
  full_transcript?: string;
  previous_chips_hash?: string;
  enabled_categories?: SuggestedChipCategory[];
  max_chips?: number;
  /** Set to true when refreshing page on existing session */
  is_page_refresh?: boolean;
  /** Patient profile ID for patient-context chips (when no transcript available) */
  patient_profile_id?: string;
}

export interface TranscriptChipsResponse {
  chips: SuggestedChip[];
  has_update: boolean;
  chips_hash?: string;
  /**
   * FDE-8: library-grounding source references for the returned chips. Keyed
   * from `chip.source_ref_ids`. Empty when `is_library_grounded` is false.
   */
  source_refs?: SourceRef[];
  /**
   * FDE-8: true when this batch of chips was grounded on a vector search over
   * the user's Evidence Library (per-team + per-user opt-in gated behind the
   * `release-ml-scribe-library-grounded-chips` Flagsmith flag).
   */
  is_library_grounded?: boolean;
}

/**
 * Origin bucket for pinned queries / shortcuts. Used purely for analytics:
 * - 'default': Heidi-provided preset (server-assigned).
 * - 'library': installed from the shortcut marketplace (includes team shortcuts).
 * - 'custom':  user authored their own shortcut.
 *
 * Clients should only ever write 'library' or 'custom'; the server coerces
 * anything else (including 'default') to 'custom' on write.
 */
export type PinnedQueryOrigin = 'default' | 'library' | 'custom';

/**
 * User-defined pinned query (for adding new custom pins)
 */
export interface PinnedQuery {
  label: string;
  query: string;
  /** Optional subtitle in shortcut marketplace (custom shortcuts only) */
  description?: string;
  /** Optional team shortcut reference id backing this pin */
  team_shortcut_id?: string;
  /**
   * Origin of this shortcut. Clients may send 'library' or 'custom'; omit
   * for legacy/unknown (server defaults to 'custom').
   */
  origin?: Exclude<PinnedQueryOrigin, 'default'>;
}

export type ChipShowPhase = 'before_session' | 'in_session' | 'after_session';

/**
 * Effective pinned query returned from backend (includes defaults + custom)
 */
export interface EffectivePinnedQuery {
  id: string;
  label: string;
  query: string;
  category: SuggestedChipCategory | null;
  is_default: boolean;
  /** Team shortcut reference id when this pin is team-managed */
  team_shortcut_id?: string;
  /** When this chip should be shown (before_session, in_session, after_session) */
  show_phases?: ChipShowPhase[];
  /** Optional subtitle in shortcut marketplace (custom shortcuts only) */
  description?: string;
  /**
   * Forced to 'default' for default pins. Optional on the wire because legacy
   * docs persisted before this field existed may omit it; consumers should
   * fall back to `'custom'` (mirrors the backend default).
   */
  origin?: PinnedQueryOrigin;
}

/** Classify an effective pin by source. */
export const pinKind = (p: EffectivePinnedQuery): 'default' | 'team' | 'personal' => {
  if (p.is_default) return 'default';
  if (p.team_shortcut_id) return 'team';
  return 'personal';
};

export interface AutoEvidenceConfig {
  enabled: boolean;
  enabled_categories: SuggestedChipCategory[];
  /** Effective list of pinned queries (defaults + user-defined pins) */
  pinned_queries: EffectivePinnedQuery[];
  /** IDs of default pins the user has dismissed */
  dismissed_default_ids: string[];
  /**
   * FDE-8: user-level opt-in to library-grounded chip suggestions. The flag +
   * team opt-in gate is evaluated independently on the backend — this toggle
   * lets an individual clinician turn the behaviour off without team admin
   * involvement. Defaults to `true` server-side.
   */
  library_grounding_enabled?: boolean;
  /**
   * FDE-8: optional scoping of library grounding to a subset of the user's
   * collections. `null` / omitted means "all collections the user can see".
   */
  library_grounding_collection_ids?: string[] | null;
}

export interface AutoEvidenceConfigUpdate {
  enabled?: boolean;
  enabled_categories?: SuggestedChipCategory[];
  /** User's pinned queries (not defaults) */
  pinned_queries?: PinnedQuery[];
  /** IDs of default pins to dismiss */
  dismissed_default_ids?: string[];
  /** FDE-8: toggle library-grounded chip suggestions for this user */
  library_grounding_enabled?: boolean;
  /** FDE-8: restrict library grounding to these collection ids */
  library_grounding_collection_ids?: string[] | null;
}

export interface SessionChip {
  id: string;
  label: string;
  query: string;
  category: string;
}

export interface SessionChipsResponse {
  chips: SessionChip[];
  chip_to_chat: Record<string, string>;
  last_updated?: string;
}

export interface LinkChipToChatRequest {
  session_id: string;
  chip_id: string;
  chat_id: string;
}

/**
 * Default Evidence config for new users or when the config read fails.
 *
 * A FROZEN FALLBACK, not a convenience default, and that is why it is in the
 * inert zone rather than beside the read that returns it. Two consumers depend
 * on the exact list: the carved read hands it back on any error (so the chip
 * rail renders instead of erroring), and the carved write spreads it as the base
 * of its optimistic cache entry. A category added or dropped here changes what a
 * clinician sees AND what the optimistic entry claims the server holds.
 *
 * `pinned_queries` is empty on purpose — the backend supplies the defaults.
 */
export const DEFAULT_EVIDENCE_CONFIG: AutoEvidenceConfig = {
  enabled: true,
  enabled_categories: [
    'red_flag',
    'symptom_exploration',
    'suggested_question',
    'examination',
    'investigation',
    'condition',
    'plan',
    'clinical_review',
  ],
  pinned_queries: [], // Backend will provide defaults
  dismissed_default_ids: [],
  // FDE-8: default to on for users. Team admins can flip team-level opt-in
  // off, and the Flagsmith release flag still gates actual evaluation.
  library_grounding_enabled: true,
  library_grounding_collection_ids: null,
};
